// src/components/admin/PalletCatalogPage.js
import React, { useState, useEffect } from 'react';
import { 
  Card, Form, Input, InputNumber, Button, Table, Select, 
  message, Popconfirm, Space, Typography, Tag, Tooltip, Modal 
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { catalogService } from '../../services/api'; // Importar el servicio
import { useAuth } from '../../context/AuthContext'; // --- PERMISOS: Se importa el hook

const { Title } = Typography;
const { Option } = Select;

const PalletCatalogPage = () => {
  const { user } = useAuth(); // --- PERMISOS: Se obtiene el usuario
  const [form] = Form.useForm();
  const [pallets, setPallets] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPallet, setEditingPallet] = useState(null); 

  // --- PERMISOS: Se definen constantes para los permisos
  const permissions = user?.role?.profile?.permissions;
  const canViewPage = permissions?.pages?.viewAdminPallets;
  const canCreate = permissions?.actions?.palletCatalog?.canCreatePallet;
  const canEdit = permissions?.actions?.palletCatalog?.canEditPallet;
  const canDelete = permissions?.actions?.palletCatalog?.canDeletePallet;

  useEffect(() => {
    // --- PERMISOS: Solo se cargan los datos si tiene permiso para ver la página
    if (canViewPage) {
      fetchPallets();
    }
  }, [canViewPage]);

  const fetchPallets = async () => {
    setLoading(true);
    try {
      const response = await catalogService.getAllPalletDefinitionsForAdmin();
      if (response.data.success) {
        setPallets(response.data.data);
      } else {
        message.error(response.data.message || 'Error al cargar catálogo de tarimas');
        setPallets([]);
      }
    } catch (error) {
      console.error('Error al cargar catálogo de tarimas:', error);
      setPallets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPallet = () => {
    setEditingPallet(null); 
    form.resetFields(); 
    // +++ Se establece el valor por defecto para el nuevo campo 'type' +++
    form.setFieldsValue({ status: 'activo', type: 'TARIMA' });
    setIsModalOpen(true); 
  };

  const handleEditPallet = (record) => {
    setEditingPallet(record);
    form.setFieldsValue({
      dimensions: record.dimensions,
      description: record.description,
      weight: record.weight,
      status: record.status,
      // +++ Se añade el campo 'type', con un valor por defecto para registros antiguos +++
      type: record.type || 'TARIMA',
    });
    setIsModalOpen(true);
  };

  const handleDeletePallet = async (palletId) => {
    setLoading(true);
    try {
      const response = await catalogService.deletePalletDefinition(palletId);
      if (response.data.success) {
        message.success('Definición eliminada correctamente');
        fetchPallets(); 
      } else {
        message.error(response.data.message || 'Error al eliminar la definición');
      }
    } catch (error) {
      console.error('Error al eliminar la definición:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePallet = async (values) => {
    setLoading(true);
    const payload = { ...values };
    
    try {
      let response;
      if (editingPallet) { 
        response = await catalogService.updatePalletDefinition(editingPallet._id, payload);
      } else { 
        response = await catalogService.createPalletDefinition(payload);
      }

      if (response.data.success) {
        message.success(`Definición ${editingPallet ? 'actualizada' : 'guardada'} correctamente`);
        setIsModalOpen(false);
        setEditingPallet(null);
        form.resetFields();
        fetchPallets(); 
      } else {
        message.error(response.data.message || `Error al ${editingPallet ? 'actualizar' : 'guardar'} la definición`);
      }
    } catch (error) {
      console.error(`Error al ${editingPallet ? 'actualizar' : 'guardar'} la definición:`, error);
    } finally {
      setLoading(false);
    }
  };


  const columns = [
    { 
      title: 'Dimensiones', 
      dataIndex: 'dimensions', 
      key: 'dimensions', 
      sorter: (a,b) => a.dimensions.localeCompare(b.dimensions) 
    },
    // +++ INICIO: NUEVA COLUMNA 'TIPO' +++
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (type) => {
          const defaultType = type || 'TARIMA';
          const color = defaultType === 'CAJA' ? 'gold' : 'geekblue';
          return <Tag color={color}>{defaultType}</Tag>;
      },
      filters: [
          { text: 'TARIMA', value: 'TARIMA' },
          { text: 'CAJA', value: 'CAJA' },
      ],
      onFilter: (value, record) => (record.type || 'TARIMA').indexOf(value) === 0,
    },
    // +++ FIN: NUEVA COLUMNA 'TIPO' +++
    { 
      title: 'Descripción', 
      dataIndex: 'description', 
      key: 'description',
      sorter: (a,b) => a.description.localeCompare(b.description) 
    },
    { 
      title: 'Peso máximo (KG)', 
      dataIndex: 'weight', 
      key: 'weight', 
      align: 'right', 
      sorter: (a,b) => a.weight - b.weight 
    },
    { 
      title: 'Estatus', 
      dataIndex: 'status', 
      key: 'status',
      width: 110,
      render: (status) => <Tag color={status === 'activo' ? 'green' : 'red'}>{status ? status.toUpperCase() : 'N/A'}</Tag>,
      filters: [
        { text: 'Activo', value: 'activo' },
        { text: 'Inactivo', value: 'inactivo' },
      ],
      onFilter: (value, record) => record.status.indexOf(value) === 0,
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          {canEdit && (
            <Tooltip title="Editar">
              <Button type="primary" shape="circle" icon={<EditOutlined />} onClick={() => handleEditPallet(record)} />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm
              title="¿Está seguro de eliminar esta definición?"
              onConfirm={() => handleDeletePallet(record._id)}
              okText="Sí"
              cancelText="No"
              placement="topRight"
            >
              <Tooltip title="Eliminar">
                <Button type="primary" danger shape="circle" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (!canViewPage) {
    return (
      <Card title={<Title level={4}>Catálogo de Tarimas</Title>}>
        <p>No tiene permiso para acceder a este catálogo.</p>
      </Card>
    );
  }

  return (
    <Card title={<Title level={4}>Catálogo de Tarimas</Title>}>
      {canCreate && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddPallet}
          style={{ marginBottom: 16 }}
        >
          Agregar Nueva Definición
        </Button>
      )}

      <Modal
        title={editingPallet ? "Editar Definición" : "Agregar Nueva Definición"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingPallet(null);
          form.resetFields();
        }}
        footer={null} 
        destroyOnClose 
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSavePallet}
          initialValues={{ status: 'activo', type: 'TARIMA' }} 
        >
          <Form.Item
            name="dimensions"
            label="Dimensiones"
            rules={[{ required: true, message: 'Por favor ingrese las dimensiones (ej. 120x100x15)' }]}
          >
            <Input placeholder="Ej: 120x100x15 o Caja Chica (40x30x30)" />
          </Form.Item>
          {/* +++ INICIO: NUEVO CAMPO 'TIPO' EN EL FORMULARIO +++ */}
          <Form.Item
            name="type"
            label="Tipo"
            rules={[{ required: true, message: 'Por favor seleccione un tipo' }]}
          >
            <Select placeholder="Seleccione el tipo de elemento">
              <Option value="TARIMA">TARIMA</Option>
              <Option value="CAJA">CAJA</Option>
            </Select>
          </Form.Item>
          {/* +++ FIN: NUEVO CAMPO 'TIPO' EN EL FORMULARIO +++ */}
          <Form.Item
            name="description"
            label="Descripción"
            rules={[{ required: true, message: 'Por favor ingrese una descripción' }]}
          >
            <Input.TextArea rows={2} placeholder="Descripción breve de la tarima o caja" />
          </Form.Item>
          <Form.Item
            name="weight"
            label="Peso máximo(KG)"
            rules={[{ required: true, type: 'number', min: 0, message: 'Por favor ingrese un peso válido (mayor o igual a 0)' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="Peso máximo en KG" />
          </Form.Item>
          <Form.Item
            name="status"
            label="Estatus"
            rules={[{ required: true, message: 'Por favor seleccione un estatus' }]}
          >
            <Select placeholder="Seleccione un estatus">
              <Option value="activo">Activo</Option>
              <Option value="inactivo">Inactivo</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {editingPallet ? "Actualizar Definición" : "Guardar Definición"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Table
        columns={columns}
        dataSource={pallets}
        rowKey="_id" 
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }} 
      />
    </Card>
  );
};

export default PalletCatalogPage;
